import { getClaims, updateStorageProfile } from "../tokenHelper";

interface ClaimType {
    [key: string]: string;
}

const claims: ClaimType = {
    DEFAULT_PAGE: "default-page",
    DISPLAY_NAME: "name",
    DASHBOARD_PERMISSION : "dashboard-permission",
    EMAIL: "email",
    FIRST_NAME: "first_name",
    LAST_NAME: "last_name",
    PHONE: "phone",
    PERMISSION: "permission",
    ROLE: "role",
    USER_ID: "sub",
    CAMPAIGN_ID: "campaign-id",
    CONTRIBUTOR_ID: "contributor-id",
    ORGANIZATION_ID: "organization-id",
    ORGANIZATION_NAME: "organization-name",
    PLEDGE_PAYMENT_TYPE: "pledge-payment-type",
    USER_TYPE: "user-type",
};

const permissions: ClaimType = {
    ADMIN_DONOR_LOGIN: "admin-donor-admin",
    ADMIN_LOGIN: "admin-login",
    ADMIN_LOOKUP: "admin-lookup",
    ADMIN_ORGANIZATION: "admin-organization",
    ADMIN_SYSTEM: "admin-system",
    ADMIN_USER: "admin-user",
    CAN_VIEW_LEADERSHIP_DASHBOARD:"can-view-leadership-dashboard",
    CORPORATE_USER: "corporate-user",
    DELETE_ADMIN_USER: "delete-admin-user",
    DELETE_DONOR_ADMIN: "delete-donor-admin",
    DELETE_ORGANIZATION: "delete-organization",
    EMPLOYEE_PLEDGE: "employee-pledge",
    IMPORT_EMPLOYEE_DATA: "import-employee-data",
    MANAGE_ADMIN_USER: "manage-admin-user",
    MANAGE_AGENCIES: "manage-agencies",   
    MANAGE_CAMPAIGNS: "manage-campaigns",
    MANAGE_DONOR_ADMIN: "manage-donor-admin",
    MANAGE_EMPLOYEES: "manage-employees",
    MANAGE_LOOKUPS: "manage-lookups",
    MANAGE_NOTIFICATIONS: "manage-notifications",
    MANAGE_ORGANIZATION: "manage-organization",
    VIEW_CONTRIBUTIONS_BY_CHECK: "view-contributions-by-check",
    VIEW_EDIT_PLEDGE: "view-edit-pledge",
    VIEW_EXCEL_REPORTS: "view-excel-reports",
    VIEW_REPORTS: "view-reports",
};

const roles: ClaimType = {
    SYSTEM_ADMIN: "system-admin",
    SUPER_ADMIN: "super-admin",
    ORGANIZATION_ADMIN: "organization-admin",
    CAUW_USER: "cauw-user",
};

const userType: ClaimType = {
    CAUW_USER: "cauw-user",
    ORGANIZATION_USER: "organization-user",
    ORGANIZATION_DONOR: "organization-donor",
};

const pledgePaymentTypes: ClaimType = {
    CHECK: "check",
    CREDIT_CARD: "credit-card",
    PAYROLL_DEDUCTION: "payroll-deduction",
};

const dashboardTypes: ClaimType = {
    ORGANIZATION: "organization",
    LEADERSHIP: "leadership",
};

const hasPermission = (requiredClaims: string[] | null | undefined): boolean => {
    return hasClaim("permission", requiredClaims);
};

const hasDashboardPermission = (requiredClaims: string[] | null | undefined ): boolean => {
    return hasClaim("dashboard-permission", requiredClaims);
};

const hasPaymentTypePermission = (requiredClaims: string[] | null | undefined): boolean => {
    return hasClaim("pledge-payment-type", requiredClaims);
};

const hasRole = (requiredClaims: string[] | null | undefined): boolean => {
    return hasClaim("role", requiredClaims);
};

const hasClaim = (claimType: string, requiredClaims: string[] | null | undefined): boolean => {
    const claims = getClaims();
    const targetClaims = claims?.[claimType];
    let userHasPermission = false;

    if (typeof targetClaims === "string") {
        if (requiredClaims) {
            userHasPermission = requiredClaims?.indexOf(targetClaims) >= 0;
        }
    } else {
        if (requiredClaims) {
            userHasPermission = targetClaims?.some( (r: any) => requiredClaims.indexOf(r) >= 0 ) ?? false;
        }
    }
    return userHasPermission;
};

const doesNotHaveRole = (
    requiredRoles: string[] | null | undefined
): boolean => {
    return !hasRole(requiredRoles);
};

const getClaimStringValue = (claimType: string): string => {
    const userClaims = getClaims();
    if (!userClaims) {
        return "";
    }

    const claimValue = userClaims[claimType];
    if (claimValue !== null && claimValue !== undefined) {
        return claimValue;
    }

    return "";
};

const getClaimBoolValue = (claimType: string): boolean => {
    const userClaims = getClaims();
    if (!userClaims) {
        return false;
    }

    const claimValue = userClaims[claimType];
    if (claimValue !== null && claimValue !== undefined) {
        return (
            claimValue.toLowerCase() === "true" ||
            claimValue.toLowerCase() === "0" ||
            claimValue.toLowerCase() === "yes" ||
            claimValue === true
        );
    }

    return false;
};

const getUserName = (): string => {
    const firstName: string = getClaimStringValue(claims.FIRST_NAME);
    const lastName: string = getClaimStringValue(claims.LAST_NAME);

    return firstName + " " + lastName;
};

const getUserInitials = (): string => {
    const firstName: string = getClaimStringValue(claims.FIRST_NAME);
    const lastName: string = getClaimStringValue(claims.LAST_NAME);
    var initials = "";

    if (firstName !== "" && firstName !== undefined) {
        initials = Array.from(firstName)[0];
    }

    if (lastName !== "" && lastName !== undefined) {
        initials = initials + Array.from(lastName)[0];
    }
    return initials;
};

const updateProfileDisplayName = (
    firstName: string,
    lastName: string
): void => {
    const userClaims = getClaims();
    if (!userClaims) return;

    userClaims[claims.LAST_NAME] = lastName;
    userClaims[claims.FIRST_NAME] = firstName;
    updateStorageProfile(userClaims);
};

const getOrganizationName = (): string => {
    const organizationName: string = getClaimStringValue(claims.ORGANIZATION_NAME);
    return organizationName;
};

const getOrganizationId = (): string => {
    const organizationId: string = getClaimStringValue(claims.ORGANIZATION_ID);
    return organizationId;
};

const getContributorId = (): string => {
    const contributorId: string = getClaimStringValue(claims.CONTRIBUTOR_ID);
    if (contributorId === null || contributorId === undefined || contributorId === "") {
        return getClaimStringValue(claims.USER_ID);
    }
    return contributorId;
};

const getCampaignId = (): string => {
    const organizationId: string = getClaimStringValue(claims.CAMPAIGN_ID);
    return organizationId;
};

const IsCauwUser = (): boolean => {
    return isUserType(userType.CAUW_USER);
};

const IsOrganizationUser = (): boolean => {
    return isUserType(userType.ORGANIZATION_USER);
};

const IsOrganizationDonor = (): boolean => {
    return isUserType(userType.ORGANIZATION_DONOR);
};

function isUserType(userType: string) {
    var value: string = getUserType();
    return value !== null && value !== undefined && value === userType;
}

const getUserType = () => {
    const userType: string = getClaimStringValue(claims.USER_TYPE);
    return userType;
};

export {
    claims,
    permissions,
    roles,
    dashboardTypes,
    pledgePaymentTypes,
    getClaimStringValue,
    getClaimBoolValue,
    hasPermission,
    hasDashboardPermission,
    hasPaymentTypePermission,
    hasRole,
    doesNotHaveRole,
    updateProfileDisplayName,
    getUserName,
    getUserInitials,
    getOrganizationName,
    getOrganizationId,
    IsCauwUser,
    IsOrganizationUser,
    IsOrganizationDonor,
    getContributorId,
    getCampaignId,
    userType
};
